import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Doctor, Patient, TeleVisitQueueItem, StoreListItem } from '../../types/api';
import { apiService } from '../../services/api';
import { QueueTriageSummary } from '../../components/queue/QueueTriageSummary';
import { NewPatientBadge } from '../../components/queue/NewPatientBadge';
import { CorporateCompanyBadge } from '../../components/queue/CorporateCompanyBadge';
import { ConsultPartnerCodeBadge } from '../../components/queue/ConsultPartnerCodeBadge';
import { ChasBadge, HssAppliedChip } from '../../components/hss/ChasBadge';
import { MeetPresenceBadge } from '../../components/queue/MeetPresenceBadge';
import { parseTriageSummary } from '../../utils/parseTriageSummary';
import { isConsultFinished, isCheckedOutVisit } from '../../utils/visitStage';
import { getApiErrorMessage } from '../../utils/apiError';
import { getTodayISO, formatLongDateSG } from '../../utils/date';
import { AspenHealthLogo } from '../../components/AspenHealthLogo';
import { useTeleVisitQueueRealtime } from '../../hooks/useTeleVisitQueueRealtime';
import {
  defaultTeleconsultStoreFilter,
  filterStoresForTeleconsultQueue,
  formatTeleconsultQueueStoreLabel,
  isTeleconsultQueueStoreLocked,
  resolveTeleconsultQueueStoreId,
} from '../../config/teleconsultQueueStore';
import { StaffManagementPanel, useManagementAccess } from '../staff-management';

interface QueuePageProps {
  doctor: Doctor | null;
  onSelectPatient: (patient: Patient) => void;
  onLogout: () => void;
}

const VISIT_TYPE_TELE_MED_FULFILLMENT = 991;

function isMedFulfillmentVisitType(visitType?: number | null): boolean {
  return visitType === VISIT_TYPE_TELE_MED_FULFILLMENT;
}

function mapQueueItemsToPatients(items: TeleVisitQueueItem[], idOffset: number): Patient[] {
  return items.map((item, index) => ({
    id: idOffset + index + 1,
    visitULID: item.visitULID.toString(),
    queueNo: item.queueNo,
    name: item.patientName,
    nric: item.nric,
    age: item.age,
    gender: (item.gender === 'M' || item.gender === 'F') ? item.gender : 'M' as 'M' | 'F',
    store: item.storeName,
    storeId: item.storeID,
    storeCode: item.storeCode,
    visitTime: item.visitTime,
    temp: item.temperature ?? 0,
    sbp: item.sbp ?? 0,
    dbp: item.dbp ?? 0,
    pulse: item.pulse ?? 0,
    spo2: item.spO2 ?? 0,
    weight: 0,
    height: 0,
    waist: 0,
    remarks: item.remarks || '',
    allergies: item.allergies || [],
    chronicConditions: item.chronicConditions || [],
    triageSummary: item.triageSummary ?? undefined,
    visitStage: item.visitStage,
    visitType: item.visitType,
    walkIn: item.walkIn,
    teleMed: item.teleMed,
    newPt: item.newPt,
    corporateCompanyName: item.corporateCompanyName,
    consultPartnerCode: item.consultPartnerCode ?? undefined,
    chas: item.chas ?? null,
    hssApplied: item.hssApplied ?? false,
    empId: item.empId ?? undefined,
    empCode: item.empCode ?? undefined,
    patientInRoom: item.patientInRoom ?? null,
  }));
}

function isUnpickedVisit(empId: number | null | undefined): boolean {
  return empId == null || empId === 0;
}

/** Doctor pick: offsite and in-clinic (onsite) tele. Never VisitType 991 (clinic med fulfillment). */
function isDoctorPickableQueueVisit(p: Patient): boolean {
  return !isMedFulfillmentVisitType(p.visitType);
}

function queueNumberSortKey(queueNo?: string): number {
  const n = parseInt(String(queueNo ?? '').replace(/\D/g, ''), 10);
  return Number.isFinite(n) ? n : 0;
}

/** Latest pick / not-yet-consulted first so the current patient is at the top of My picked patients. */
function sortMyPickedPatients(list: Patient[]): Patient[] {
  return [...list].sort((a, b) => {
    const aPending = isConsultFinished(a.visitStage) ? 1 : 0;
    const bPending = isConsultFinished(b.visitStage) ? 1 : 0;
    if (aPending !== bPending) return aPending - bPending;
    return queueNumberSortKey(b.queueNo) - queueNumberSortKey(a.queueNo);
  });
}

type DoctorQueueView = 'queue' | 'management';

function QueuePage({ doctor, onSelectPatient, onLogout }: QueuePageProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [availablePatients, setAvailablePatients] = useState<Patient[]>([]);
  const [myPatients, setMyPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [finishNotice, setFinishNotice] = useState<string | null>(null);
  const [pickingVisitULID, setPickingVisitULID] = useState<string | null>(null);
  const [unpickingVisitULID, setUnpickingVisitULID] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(getTodayISO());
  const [storeFilter, setStoreFilter] = useState<string>(defaultTeleconsultStoreFilter());
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const [stores, setStores] = useState<StoreListItem[]>([]);
  const [queueView, setQueueView] = useState<DoctorQueueView>('queue');
  const showManagementNav = useManagementAccess(doctor?.code, doctor?.roleId, doctor?.username);

  useEffect(() => {
    const notice = (location.state as { queueNotice?: string } | null)?.queueNotice;
    if (!notice) return;
    setFinishNotice(notice);
    setQueueView('queue');
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const storeList = filterStoresForTeleconsultQueue(await apiService.getStores());
        setStores(storeList);
      } catch (err) {
        console.error('QueuePage: Error loading stores:', err);
      }
    };
    loadStores();
  }, []);

  const loadQueues = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }
      const storeId = resolveTeleconsultQueueStoreId(storeFilter);
      const doctorId = doctor?.id;
      const [availableItems, mineItems] = await Promise.all([
        apiService.getTeleVisitQueue(selectedDate, storeId, 'available'),
        apiService.getTeleVisitQueue(selectedDate, storeId, 'mine'),
      ]);
      const availPatients = mapQueueItemsToPatients(availableItems, 0).filter(
        (p) =>
          isUnpickedVisit(p.empId) &&
          !isCheckedOutVisit(p.visitStage) &&
          isDoctorPickableQueueVisit(p),
      );
      const minePatientsRaw = mapQueueItemsToPatients(mineItems, 10_000);
      const minePatients =
        doctorId == null
          ? []
          : sortMyPickedPatients(
              minePatientsRaw.filter((p) => p.empId === doctorId && isDoctorPickableQueueVisit(p)),
            );
      setAvailablePatients(availPatients);
      setMyPatients(minePatients);
      if (silent) setError(null);
    } catch (err: unknown) {
      console.error('QueuePage: Error loading queue:', err);
      if (!silent) {
        setError(getApiErrorMessage(err, 'Could not load the queue. Please try again.'));
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedDate, storeFilter, doctor?.id]);

  useEffect(() => {
    void loadQueues();
  }, [loadQueues]);

  const applyMeetPresence = useCallback((visitUlid: string, patientInRoom: boolean) => {
    const id = visitUlid.trim().toLowerCase();
    if (!id) return;
    const patch = (list: Patient[]) =>
      list.map((p) => (p.visitULID.toLowerCase() === id ? { ...p, patientInRoom } : p));
    setAvailablePatients(patch);
    setMyPatients(patch);
  }, []);

  useTeleVisitQueueRealtime(() => loadQueues({ silent: true }), {
    enabled: Boolean(doctor?.id),
    onMeetPresenceUpdated: (payload) => {
      if (typeof payload.visitUlid !== 'string' || typeof payload.patientInRoom !== 'boolean') return;
      applyMeetPresence(payload.visitUlid, payload.patientInRoom);
    },
  });

  const filterBySearch = (list: Patient[]) =>
    list.filter((p) => {
      if (searchTerm === '') return true;
      const q = searchTerm.toLowerCase();
      const triageHaystack = [p.triageSummary ?? '', ...parseTriageSummary(p.triageSummary)].join(' ').toLowerCase();
      return p.name.toLowerCase().includes(q) || p.nric.toLowerCase().includes(q) || triageHaystack.includes(q);
    });

  const filteredAvailable = filterBySearch(availablePatients);
  const filteredMy = filterBySearch(myPatients);

  const handlePick = async (e: React.MouseEvent, visitULID: string) => {
    e.stopPropagation();
    setActionMessage(null);
    setPickingVisitULID(visitULID);
    try {
      await apiService.pickVisit(visitULID);
      await loadQueues();
    } catch (err: unknown) {
      setActionMessage(
        getApiErrorMessage(
          err,
          'Could not pick this visit. It may already be assigned to another doctor.',
        ),
      );
    } finally {
      setPickingVisitULID(null);
    }
  };

  const handleUnpick = async (e: React.MouseEvent, visitULID: string) => {
    e.stopPropagation();
    setActionMessage(null);
    setUnpickingVisitULID(visitULID);
    try {
      const ok = await apiService.unpickVisit(visitULID);
      if (!ok) {
        setActionMessage('Could not unpick — you can only unpick visits you picked.');
        return;
      }
      await loadQueues();
    } catch (err: unknown) {
      setActionMessage(getApiErrorMessage(err, 'Could not unpick this visit. Please try again.'));
    } finally {
      setUnpickingVisitULID(null);
    }
  };

  const getStageColor = (stage?: number): { background: string; border: string } => {
    switch (stage) {
      case 10:
        return { background: '#eff6ff', border: '#93c5fd' };
      case 20:
        return { background: '#fffbeb', border: '#fcd34d' };
      case 30:
        return { background: '#fff1f2', border: '#fca5a5' };
      case 60:
        return { background: '#f0fdf4', border: '#86efac' };
      default:
        return { background: '#1e293b', border: '#475569' };
    }
  };

  const getStageTextColor = (stage?: number): string => {
    switch (stage) {
      case 10:
        return '#1e40af';
      case 20:
        return '#92400e';
      case 30:
        return '#9f1239';
      case 60:
        return '#166534';
      default:
        return '#f1f5f9';
    }
  };

  const getStageSecondaryTextColor = (stage?: number): string => {
    switch (stage) {
      case 10:
        return '#1e3a8a';
      case 20:
        return '#78350f';
      case 30:
        return '#881337';
      case 60:
        return '#14532d';
      default:
        return '#cbd5e1';
    }
  };

  const renderPatientCard = (patient: Patient, variant: 'available' | 'mine') => {
    const stageColors = getStageColor(patient.visitStage);
    const textColor = getStageTextColor(patient.visitStage);
    const secondaryTextColor = getStageSecondaryTextColor(patient.visitStage);
    const isLightBg = patient.visitStage === 10 || patient.visitStage === 20 || patient.visitStage === 30 || patient.visitStage === 60;
    const busyPick = pickingVisitULID === patient.visitULID;
    const busyUnpick = unpickingVisitULID === patient.visitULID;
    const consultFinished = isConsultFinished(patient.visitStage);
    const checkedOut = isCheckedOutVisit(patient.visitStage);

    return (
      <div
        key={patient.visitULID}
        className={`patient-card patient-card--queue ${variant === 'available' ? 'patient-card--available' : 'patient-card--mine'}`}
        onClick={variant === 'mine' ? () => onSelectPatient(patient) : undefined}
        style={{
          background: stageColors.background,
          borderColor: stageColors.border,
          borderWidth: '2px',
          color: textColor,
          transition: 'all 0.2s ease',
        }}
        role={variant === 'mine' ? 'button' : undefined}
        tabIndex={variant === 'mine' ? 0 : undefined}
        onKeyDown={
          variant === 'mine'
            ? (ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') {
                  ev.preventDefault();
                  onSelectPatient(patient);
                }
              }
            : undefined
        }
      >
        <div className="patient-card__top">
          <div className="patient-card__meta">
            <div className="queue-number-wrap">
              <div className="queue-number queue-number--sm">{patient.queueNo}</div>
            </div>
            <div
              className="store-badge store-badge--queue"
              style={
                isLightBg
                  ? {
                      background: 'rgba(0,0,0,0.08)',
                      color: textColor,
                      border: `1px solid ${stageColors.border}`,
                    }
                  : undefined
              }
            >
              {patient.storeCode || patient.store}
            </div>
              {patient.newPt ? <NewPatientBadge size="sm" /> : null}
              {patient.corporateCompanyName ? (
                <CorporateCompanyBadge name={patient.corporateCompanyName} size="sm" />
              ) : null}
              {patient.consultPartnerCode ? (
                <ConsultPartnerCodeBadge code={patient.consultPartnerCode} size="sm" />
              ) : null}
              {patient.chas?.known ? <ChasBadge chas={patient.chas} size="sm" /> : null}
              {patient.hssApplied ? <HssAppliedChip /> : null}
          </div>
          {variant === 'available' && (
            <div className="patient-card__action">
              <button
                type="button"
                className="patient-card__action-btn"
                disabled={!!pickingVisitULID || busyPick}
                onClick={(e) => void handlePick(e, patient.visitULID)}
                aria-busy={busyPick}
              >
                {busyPick ? '…' : 'Pick'}
              </button>
            </div>
          )}
          {variant === 'mine' && (
            <div className="patient-card__action">
              {checkedOut ? (
                <button
                  type="button"
                  className="patient-card__action-btn patient-card__action-btn--view-details"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectPatient(patient);
                  }}
                >
                  View Details
                </button>
              ) : (
                <button
                  type="button"
                  className="patient-card__action-btn patient-card__action-btn--unpick"
                  disabled={!!unpickingVisitULID || busyUnpick || consultFinished}
                  onClick={(e) => void handleUnpick(e, patient.visitULID)}
                  aria-busy={busyUnpick}
                  title={
                    consultFinished
                      ? 'Consult finished — unpick is no longer available. Proceed to Julius for checkout.'
                      : 'Return this visit to the available queue'
                  }
                >
                  {busyUnpick ? '…' : 'Unpick'}
                </button>
              )}
            </div>
          )}
        </div>
        {variant === 'mine' && (
          <p className="patient-card__hint">
            {checkedOut ? 'Checked out — view visit details' : 'Tap card to open consult'}
          </p>
        )}
        <div className="patient-name" style={{ color: textColor, fontWeight: 500 }}>
          {patient.name}
        </div>
        {!checkedOut ? (
          <div style={{ margin: '6px 0 4px' }}>
            <MeetPresenceBadge inRoom={patient.patientInRoom} />
          </div>
        ) : null}
        <div className="patient-nric" style={{ color: isLightBg ? secondaryTextColor : '#94a3b8', opacity: isLightBg ? 0.7 : 1 }}>
          {patient.nric} • {patient.age}yo {patient.gender} • {patient.visitTime}
        </div>

        {patient.allergies.length > 0 && (
          <div
            style={{
              background: '#dc2626',
              color: '#ffffff',
              padding: '4px 8px',
              borderRadius: 6,
              fontSize: 12,
              marginBottom: 8,
              display: 'inline-block',
              fontWeight: 500,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
            }}
          >
            ⚠️ Allergies: {patient.allergies.join(', ')}
          </div>
        )}
        <QueueTriageSummary summary={patient.triageSummary} isLightBg={isLightBg} />

        <div className="vital-signs">
          <div className="vital-item">
            <span className="vital-label" style={{ color: isLightBg ? secondaryTextColor : '#cbd5e1', opacity: isLightBg ? 0.7 : 1 }}>Temp: </span>
            <span className={`vital-value ${patient.temp >= 38 ? 'abnormal' : ''}`} style={{ color: patient.temp >= 38 ? undefined : textColor, fontWeight: 500 }}>{patient.temp}°C</span>
          </div>
          <div className="vital-item">
            <span className="vital-label" style={{ color: isLightBg ? secondaryTextColor : '#cbd5e1', opacity: isLightBg ? 0.7 : 1 }}>BP: </span>
            <span className={`vital-value ${patient.sbp >= 140 ? 'abnormal' : ''}`} style={{ color: patient.sbp >= 140 ? undefined : textColor, fontWeight: 500 }}>{patient.sbp}/{patient.dbp}</span>
          </div>
          <div className="vital-item">
            <span className="vital-label" style={{ color: isLightBg ? secondaryTextColor : '#cbd5e1', opacity: isLightBg ? 0.7 : 1 }}>SpO2: </span>
            <span className={`vital-value ${patient.spo2 < 95 ? 'abnormal' : ''}`} style={{ color: patient.spo2 < 95 ? undefined : textColor, fontWeight: 500 }}>{patient.spo2}%</span>
          </div>
        </div>

        <div
          style={{
            marginTop: 12,
            fontSize: 13,
            color: isLightBg ? secondaryTextColor : '#94a3b8',
            borderTop: `1px solid ${isLightBg ? stageColors.border : '#334155'}`,
            paddingTop: 12,
            opacity: isLightBg ? 0.75 : 1,
          }}
        >
          {patient.remarks}
        </div>
      </div>
    );
  };

  return (
    <div className={`app-container${queueView === 'management' ? ' app-container--mgmt' : ''}`}>
      <button className="mobile-menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
        {sidebarOpen ? '✕' : '☰'}
      </button>
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-logo sidebar-logo--brand">
          <AspenHealthLogo variant="sidebar" />
        </div>
        <ul className="sidebar-menu">
          <li>
            <a
              href="#queue"
              className={queueView === 'queue' ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                setQueueView('queue');
                setSidebarOpen(false);
              }}
            >
              📋 Patient Queue
            </a>
          </li>
          {showManagementNav ? (
            <li>
              <a
                href="#management"
                className={queueView === 'management' ? 'active' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  setQueueView('management');
                  setSidebarOpen(false);
                }}
              >
                Management
              </a>
            </li>
          ) : null}
        </ul>
        <div className="doctor-info">
          <div className="doctor-name">{doctor?.name}</div>
          <div className="doctor-mcr">MCR: {doctor?.mcr}</div>
          <button className="btn btn-secondary" style={{ marginTop: 12, width: '100%', padding: '8px' }} onClick={onLogout}>
            Logout
          </button>
        </div>
      </aside>

      <main className="main-content">
        {queueView === 'management' ? (
          <StaffManagementPanel />
        ) : (
          <>
        <div className="page-header">
          <h1 className="page-title">Tele Visit Queue</h1>
          <div style={{ color: '#64748b' }}>
            {formatLongDateSG(selectedDate)}
          </div>
        </div>
        <div className="filter-bar">
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ padding: '8px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '14px' }}
          />
          {isTeleconsultQueueStoreLocked() ? (
            <span className="filter-bar__store-label" aria-label="Clinic">
              {formatTeleconsultQueueStoreLabel(stores)}
            </span>
          ) : (
            <select value={storeFilter} onChange={(e) => setStoreFilter(e.target.value)}>
              <option value="">All Stores</option>
              {stores.map((store) => (
                <option key={store.id} value={store.id.toString()}>
                  {store.code} - {store.storeName}
                </option>
              ))}
            </select>
          )}
          <input
            type="text"
            placeholder="Search by name or NRIC..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 150, maxWidth: 300 }}
          />
          <div className="filter-bar__summary">
            {loading ? 'Loading…' : `${filteredAvailable.length} available · ${filteredMy.length} mine`}
          </div>
        </div>

        {error && (
          <div
            style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #fecaca',
            }}
          >
            ⚠️ {error}
          </div>
        )}

        {finishNotice && (
          <div
            style={{
              background: '#ecfdf5',
              color: '#065f46',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #a7f3d0',
            }}
          >
            {finishNotice}
          </div>
        )}

        {actionMessage && (
          <div
            style={{
              background: '#fef3c7',
              color: '#92400e',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid #fcd34d',
            }}
          >
            {actionMessage}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading queue…</div>
        ) : (
          <>
            <section className="queue-section">
              <h2 className="queue-section__title">Available patients</h2>
              <p className="queue-section__lead">
                Tele visits not yet picked (no doctor assigned). Use <strong>Pick</strong> to assign a visit to yourself — other doctors will then only see it under their own picked list once they pick it.
              </p>
              <div className="patient-grid">
                {filteredAvailable.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: '#64748b', gridColumn: '1 / -1' }}>No unpicked tele visits for this date.</div>
                ) : (
                  filteredAvailable.map((p) => renderPatientCard(p, 'available'))
                )}
              </div>
            </section>

            <section className="queue-section">
              <h2 className="queue-section__title">My picked patients</h2>
              <p className="queue-section__lead">
                Visits you have picked. Tap a card to open the consult screen. Use <strong>Unpick</strong> to release a visit back to the available list.
              </p>
              <div className="patient-grid">
                {filteredMy.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '32px', color: '#64748b', gridColumn: '1 / -1' }}>You have no picked tele visits for this date.</div>
                ) : (
                  filteredMy.map((p) => renderPatientCard(p, 'mine'))
                )}
              </div>
            </section>
          </>
        )}
          </>
        )}
      </main>
    </div>
  );
}

export default QueuePage;
